const recommendationCache = new Map();

class ProductRecommendations extends HTMLElement {
  static observedAttributes = [
    'data-product-id',
    'data-recommendations-performed',
    'data-section-id',
    'data-intent',
    'data-url',
  ];

  #intersectionObserver = new IntersectionObserver(
    (entries, observer) => {
      if (!entries[0]?.isIntersecting) return;

      observer.disconnect();
      this.#loadRecommendations();
    },
    { rootMargin: '0px 0px 400px 0px' }
  );

  #activeFetch = null;
  #requestKey = null;
  #refreshFrame = null;
  #updatingMarkup = false;
  #railAbortController = null;
  #resizeObserver = null;
  #railFrame = null;

  connectedCallback() {
    if (this.dataset.recommendationsPerformed === 'true') {
      this.setAttribute('aria-busy', 'false');
      this.#initializeRail();
      return;
    }

    this.setAttribute('aria-busy', 'true');
    this.classList.remove('hidden');
    this.removeAttribute('data-error');
    this.#intersectionObserver.observe(this);
  }

  disconnectedCallback() {
    this.#intersectionObserver.disconnect();
    this.#activeFetch?.abort();
    this.#activeFetch = null;
    this.#requestKey = null;

    if (this.#refreshFrame !== null) cancelAnimationFrame(this.#refreshFrame);
    this.#refreshFrame = null;
    this.#destroyRail();
  }

  attributeChangedCallback(_name, oldValue, newValue) {
    if (!this.isConnected || this.#updatingMarkup || oldValue === newValue) return;

    if (this.#refreshFrame !== null) cancelAnimationFrame(this.#refreshFrame);
    this.#refreshFrame = requestAnimationFrame(() => {
      this.#refreshFrame = null;
      if (!this.isConnected) return;

      this.#intersectionObserver.disconnect();
      this.#activeFetch?.abort();
      this.#activeFetch = null;
      this.#requestKey = null;
      this.#destroyRail();

      if (this.dataset.recommendationsPerformed === 'true') {
        this.setAttribute('aria-busy', 'false');
        this.#initializeRail();
      } else {
        this.setAttribute('aria-busy', 'true');
        this.classList.remove('hidden');
        this.removeAttribute('data-error');
        this.#intersectionObserver.observe(this);
      }
    });
  }

  #loadRecommendations() {
    const { productId, recommendationsPerformed, sectionId, intent } = this.dataset;
    const id = this.id;

    if (!productId || !id) {
      throw new Error('Product ID and an ID attribute are required');
    }

    if (recommendationsPerformed === 'true') {
      this.#initializeRail();
      return;
    }

    const requestKey = `${this.dataset.url}|${productId}|${sectionId}|${intent}`;
    this.#requestKey = requestKey;

    this.#fetchCachedRecommendations(productId, sectionId, intent)
      .then((result) => {
        if (!this.isConnected || this.#requestKey !== requestKey) return;

        if (!result.success) {
          if (!window.Shopify?.designMode) {
            this.#handleError();
          } else {
            this.setAttribute('aria-busy', 'false');
          }
          return;
        }

        const html = document.createElement('div');
        html.innerHTML = result.data || '';
        const recommendations = html.querySelector(`product-recommendations[id="${id}"]`);

        if (recommendations?.innerHTML.trim()) {
          this.#updatingMarkup = true;
          this.dataset.recommendationsPerformed = 'true';
          this.innerHTML = recommendations.innerHTML;
          this.#updatingMarkup = false;
          this.#requestKey = null;
          this.setAttribute('aria-busy', 'false');
          this.#initializeRail();
        } else {
          this.#handleError();
        }
      })
      .catch((error) => {
        if (
          error?.name !== 'AbortError' &&
          this.isConnected &&
          this.#requestKey === requestKey
        ) {
          this.#handleError();
        }
      });
  }

  async #fetchCachedRecommendations(productId, sectionId, intent) {
    const url = `${this.dataset.url}&product_id=${productId}&section_id=${sectionId}&intent=${intent}`;
    const cachedResponse = recommendationCache.get(url);

    if (recommendationCache.has(url)) {
      return { success: true, data: cachedResponse };
    }

    this.#activeFetch?.abort();
    const controller = new AbortController();
    this.#activeFetch = controller;

    try {
      const response = await fetch(url, {
        signal: controller.signal,
        credentials: 'same-origin',
      });
      if (!response.ok) {
        return { success: false, status: response.status };
      }

      const text = await response.text();
      recommendationCache.set(url, text);
      if (recommendationCache.size > 20) {
        recommendationCache.delete(recommendationCache.keys().next().value);
      }
      return { success: true, data: text };
    } finally {
      if (this.#activeFetch === controller) this.#activeFetch = null;
    }
  }

  #initializeRail() {
    this.#destroyRail();

    const rail = this.querySelector('[data-product-recommendations-rail]');
    const track = rail?.querySelector('[data-product-recommendations-track]');
    const items = track ? [...track.querySelectorAll('[data-product-recommendations-item]')] : [];
    const controls = rail?.querySelector('[data-product-recommendations-controls]');
    const previous = rail?.querySelector('[data-product-recommendations-previous]');
    const next = rail?.querySelector('[data-product-recommendations-next]');
    const status = rail?.querySelector('[data-product-recommendations-status]');

    if (!rail || !track || !controls || !previous || !next || !status || items.length < 1) return;

    this.#railAbortController = new AbortController();
    const { signal } = this.#railAbortController;

    const itemLeft = (item) => {
      const trackRect = track.getBoundingClientRect();
      const itemRect = item.getBoundingClientRect();
      return itemRect.left - trackRect.left + track.scrollLeft;
    };

    const activeIndex = () => {
      let closestIndex = 0;
      let closestDistance = Number.POSITIVE_INFINITY;

      items.forEach((item, index) => {
        const distance = Math.abs(itemLeft(item) - track.scrollLeft);
        if (distance < closestDistance) {
          closestDistance = distance;
          closestIndex = index;
        }
      });

      return closestIndex;
    };

    const update = () => {
      this.#railFrame = null;
      const overflow = track.scrollWidth - track.clientWidth > 4;
      const index = activeIndex();
      const atStart = track.scrollLeft <= 4;
      const atEnd = track.scrollLeft + track.clientWidth >= track.scrollWidth - 4;

      controls.hidden = !overflow;
      rail.toggleAttribute('data-has-overflow', overflow);
      track.tabIndex = overflow ? 0 : -1;
      previous.disabled = !overflow || atStart;
      next.disabled = !overflow || atEnd;
      status.textContent = `${index + 1} of ${items.length}`;
    };

    const scheduleUpdate = () => {
      if (this.#railFrame !== null) cancelAnimationFrame(this.#railFrame);
      this.#railFrame = requestAnimationFrame(update);
    };

    const move = (direction) => {
      const current = activeIndex();
      const targetIndex = Math.max(0, Math.min(items.length - 1, current + direction));
      track.scrollTo({
        left: itemLeft(items[targetIndex]),
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
      });
    };

    previous.addEventListener('click', () => move(-1), { signal });
    next.addEventListener('click', () => move(1), { signal });
    track.addEventListener('scroll', scheduleUpdate, { passive: true, signal });
    track.addEventListener(
      'keydown',
      (event) => {
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
        event.preventDefault();
        move(event.key === 'ArrowRight' ? 1 : -1);
      },
      { signal }
    );

    this.#resizeObserver = new ResizeObserver(scheduleUpdate);
    this.#resizeObserver.observe(track);
    items.forEach((item) => this.#resizeObserver.observe(item));
    scheduleUpdate();
  }

  #destroyRail() {
    this.#railAbortController?.abort();
    this.#railAbortController = null;
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = null;

    if (this.#railFrame !== null) {
      cancelAnimationFrame(this.#railFrame);
      this.#railFrame = null;
    }
  }

  #handleError() {
    this.#requestKey = null;
    this.setAttribute('aria-busy', 'false');
    this.classList.add('hidden');
    this.dataset.error = 'Error loading product recommendations';
  }
}

if (!customElements.get('product-recommendations')) {
  customElements.define('product-recommendations', ProductRecommendations);
}